/**
 * Heading Widget (Story 5.24, Task 2)
 *
 * Renders a styled heading with configurable size (h1/h2/h3) and alignment.
 * Content is plain text only — no HTML rendering (XSS prevention).
 */

import type { WidgetConfig } from "../../../lib/templateUtils";

interface HeadingWidgetProps {
  widget: WidgetConfig;
}

type HeadingSize = "h1" | "h2" | "h3";
type HeadingAlign = "left" | "center" | "right";

const SIZE_CLASSES: Record<HeadingSize, string> = {
  h1: "text-2xl font-bold leading-tight",
  h2: "text-xl font-semibold leading-tight",
  h3: "text-lg font-medium leading-snug",
};

const ALIGN_CLASSES: Record<HeadingAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export function HeadingWidget({ widget }: HeadingWidgetProps) {
  const config = widget.config as { content?: string; size?: HeadingSize; align?: HeadingAlign };
  const content = config.content ?? "";
  const size = config.size ?? "h2";
  const align = config.align ?? "left";

  if (!content) {
    return (
      <div data-testid="widget-type-heading" className="flex h-full items-center p-4">
        <span className="text-[13.5px] italic text-muted-foreground" data-testid="heading-empty">
          Add heading...
        </span>
      </div>
    );
  }

  const Tag = size;
  const className = `${SIZE_CLASSES[size]} ${ALIGN_CLASSES[align]} text-foreground`;

  return (
    <div data-testid="widget-type-heading" className="flex h-full items-center p-4">
      <Tag className={`w-full ${className}`} data-testid="heading-content">
        {content}
      </Tag>
    </div>
  );
}
